import TextButton from 'LuxKit/Button/TextButton'
import DropdownSheetFooter from 'LuxKit/Dropdown/Sheet/DropdownSheetFooter'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { useCallback, useContext } from 'react'
import OfferCalenderDatePickerFooterPriceDetails from './OfferCalenderDatePickerFooterPriceDetails'
import OfferAddToCartButton from 'components/OfferPage/OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from 'components/OfferPage/OfferBookButtons/OfferSelectPackageButton'
import { OFFER_TYPE_VILLA } from 'constants/offer'
import { useIntl } from 'react-intl'

interface Props {
  canBook?: boolean
  offer: App.Offer
  selectedDay?: App.CalendarDay
  duration: number
  relevantPackages: Array<App.OfferPackageOptionView>
  selectedPackageView?: App.OfferPackageOptionView
  isAnyFlightsActive?: boolean
  isSelectedTargetUpgradePackage: boolean
  /** When true, user is viewing month picker and hasn't selected a specific month yet */
  isOnMonthPicker?: boolean
}

function OfferCalendarDatePickerDropdownFooter(props: Props) {
  const intl = useIntl()
  const {
    canBook,
    offer,
    selectedDay,
    duration,
    relevantPackages,
    selectedPackageView,
    isAnyFlightsActive,
    isSelectedTargetUpgradePackage,
    isOnMonthPicker,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()
  const hasDateSelected = !!selectedDay && selectedDay.hotelAvailable && !isOnMonthPicker
  const canAddToUserCart = hasDateSelected && !isSelectedTargetUpgradePackage && canBook && offer.type !== OFFER_TYPE_VILLA
  const canShowBookNow = hasDateSelected && canBook

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
  }, [dispatch])

  const onBookNowButtonClick = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.BookNow)
  }, [onUserAnalyticsAction])

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  return <DropdownSheetFooter
    startSlot={selectedDay && <OfferCalenderDatePickerFooterPriceDetails
      offer={offer}
      duration={duration}
      selectedDay={selectedDay}
      isAnyFlightsActive={isAnyFlightsActive}
      isFromPrice={relevantPackages.length > 1}
    />}
    actionsSlot={<>
      {!!selectedDay && <TextButton kind="tertiary" size="large" onClick={onResetDates}>
        {intl.formatMessage({
          id: 'l34vjHPep6',
          defaultMessage: 'Reset dates',
          description: 'Button text to reset selected dates in calendar picker',
        })}
      </TextButton>}
      {canAddToUserCart && <>
        {isAnyFlightsActive && <OfferSelectPackageButton
          size="large"
          data-buytype="flights"
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
        {!isAnyFlightsActive && <OfferAddToCartButton
          size="large"
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
      </>}
      {canShowBookNow && !isAnyFlightsActive && <OfferBookButton
        size="large"
        data-testid="offer-calendar-apply"
        data-buytype={isAnyFlightsActive ? 'flights' : undefined}
        packageView={selectedPackageView ?? relevantPackages[0]}
        onSubmit={onBookNowButtonClick}
      >
        {intl.formatMessage({
          id: 'IQ9I9sGCwA',
          defaultMessage: 'Book now',
          description: 'Button text to book now from calendar picker in the calendar picker',
        })}
      </OfferBookButton>}
      {!canShowBookNow && <TextButton
        kind="primary"
        size="large"
        data-testid="offer-calendar-apply"
        onClick={onContinue}
      >
        {intl.formatMessage({
          id: '3xrYWj/0p3',
          defaultMessage: 'Continue',
          description: 'Button text to continue from calendar picker in the calendar picker',
        })}
      </TextButton>}
    </>}
  />
}

export default React.memo(OfferCalendarDatePickerDropdownFooter)
